// src/components/ReviewsSection.js
import React from "react";
import "./ReviewsSection.css";

export default function ReviewsSection({ reviews = [], title = "Your Visits" }) {
  // Group multiple visits into one card per locationId
  const groupedVisits = React.useMemo(() => {
    const map = new Map();

    for (const r of reviews || []) {
      const locationId =
        r.locationId ||
        r.location_id ||
        r.location?.id ||
        r.location ||
        r.id ||
        "unknown_location";

      const key = String(locationId);
      const existing = map.get(key);

      const ts = r.timestamp || r.createdAt || r.date || null;
      const tsMs = ts ? new Date(ts).getTime() : 0;

      const ratingNum = Number(r.stars ?? r.rating ?? 0);

      // IMPORTANT:
      // If r already comes from a grouped source, it may have timesVisited already.
      // If it’s a raw visit, timesVisited will be missing -> treat as 1.
      const incVisits = Number.isFinite(Number(r.timesVisited))
        ? Number(r.timesVisited)
        : 1;

      if (!existing) {
        map.set(key, {
          ...r,
          timesVisited: incVisits,
          _latestTs: ts,
          _latestTsMs: tsMs,
          _ratingSum: Number.isFinite(ratingNum) ? ratingNum : 0,
          _ratingCount: Number.isFinite(ratingNum) && ratingNum > 0 ? 1 : 0,
        });
      } else {
        // add visits
        existing.timesVisited += incVisits;

        // rating aggregation (optional)
        if (Number.isFinite(ratingNum) && ratingNum > 0) {
          existing._ratingSum += ratingNum;
          existing._ratingCount += 1;
        }

        // keep most recent snapshot
        if (tsMs >= (existing._latestTsMs || 0)) {
          Object.assign(existing, r);
          existing._latestTs = ts;
          existing._latestTsMs = tsMs;
        }

        map.set(key, existing);
      }
    }

    // Sort newest first
    return Array.from(map.values()).sort(
      (a, b) => (b._latestTsMs || 0) - (a._latestTsMs || 0)
    );
  }, [reviews]);

  if (!groupedVisits.length) {
    return (
      <section className="p2p-reviews">
        <div className="p2p-reviews__header">
          <h3 className="p2p-reviews__title">{title}</h3>
        </div>

        {/* ✅ Panel wrapper so the section visually matches your other containers */}
        <div className="p2p-reviews__panel">
          <p className="p2p-reviews__empty">
            No visits yet. Once you use a partner bathroom, they’ll show up here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="p2p-reviews">
      <div className="p2p-reviews__header">
        <h3 className="p2p-reviews__title">{title}</h3>
        <div className="p2p-reviews__hint">Swipe →</div>
      </div>

      {/* ✅ This is what fixes the “dark space after cards” vibe */}
      <div className="p2p-reviews__panel">
        <div className="p2p-reviews__rail" role="list">
          {groupedVisits.map((r, i) => {
            // ✅ Use location name + address (NOT guest name)
            const locationName =
              r.bathroomName ||
              r.locationName ||
              r.partnerBathroomName ||
              r.businessName ||
              r.title ||
              r.name ||
              "Bathroom";

            const address =
              r.address ||
              r.locationAddress ||
              r.location?.address ||
              "";

            // rating
            const avgRating =
              r._ratingCount && r._ratingCount > 0
                ? r._ratingSum / r._ratingCount
                : Number(r.stars ?? r.rating ?? 0);

            const rating = Number.isFinite(avgRating) ? avgRating : 0;

            const comment = r.comment || r.reviewText || "";
            const dateLabel = r.timestamp ? new Date(r.timestamp).toLocaleDateString() : "";

            // fallback letter avatar = first letter of location
            const fallbackLetter = (locationName?.[0] || "B").toUpperCase();

            return (
              <div key={r.id || i} className="p2p-reviewCard" role="listitem">
                <div className="p2p-reviewCard__top">
                  <div className="p2p-reviewCard__guest">
                    <div className="p2p-reviewCard__avatarFallback">
                      {fallbackLetter}
                    </div>

                    <div className="p2p-reviewCard__guestMeta">
                      <div className="p2p-reviewCard__guestName">
                        {locationName}
                      </div>

                      <div className="p2p-reviewCard__bathroomName">
                        {address ? address : "Address not available"}

                        <span className="p2p-reviewCard__visitCount">
                          {" "}
                          · {r.timesVisited} visit{r.timesVisited === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p2p-reviewCard__rating">
                    <span className="p2p-reviewCard__star">★</span>
                    <span className="p2p-reviewCard__ratingNum">
                      {rating.toFixed(1)}
                    </span>

                    {dateLabel ? (
                      <span className="p2p-reviewCard__date">{dateLabel}</span>
                    ) : null}
                  </div>
                </div>

                {comment ? (
                  <p className="p2p-reviewCard__comment">{comment}</p>
                ) : (
                  <p className="p2p-reviewCard__comment p2p-reviewCard__comment--muted">
                    No written feedback yet. Tap a visit to leave a review.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}